import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, StarIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'in_force' | 'lapsed' | 'switched_off';

interface Approval {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  approvedVendorsOnly: boolean;
  vendorId: string;
  vendorName: string;
  vendorCode: string;
  vendorPartNumber: string | null;
  leadTimeDays: number | null;
  minimumOrderQuantity: number | null;
  isPreferred: boolean;
  approvedOn: string;
  validUntil: string | null;
  notes: string | null;
  isActive: boolean;
  status: Status;
  lastPrice: number | null;
  lastOrderedOn: string | null;
}
interface ApprovalPage {
  data: Approval[];
  total: number;
  totalPages: number;
}
interface Summary {
  itemsWithList: number;
  approvals: number;
  restrictedItems: number;
  lapsed: number;
  lapsingSoon: number;
  restrictedWithoutVendor: number;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}
interface VendorChoice {
  id: string;
  name: string;
  code: string;
  isActive?: boolean;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  in_force: { label: 'Approved', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  lapsed: { label: 'Lapsed', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  switched_off: { label: 'Switched off', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const avlApi = {
  page: async (params: object): Promise<ApprovalPage> => (await api.get('/approved-vendors', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/approved-vendors/summary')).data,
  create: async (data: object): Promise<Approval> => (await api.post('/approved-vendors', data)).data,
  update: async (id: string, data: object): Promise<Approval> => (await api.patch(`/approved-vendors/${id}`, data)).data,
  remove: async (id: string): Promise<void> => (await api.delete(`/approved-vendors/${id}`)).data,
  restrict: async (productId: string, approvedVendorsOnly: boolean) => (await api.put(`/approved-vendors/products/${productId}/restriction`, { approvedVendorsOnly })).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
  vendors: async (): Promise<VendorChoice[]> => {
    const result = (await api.get('/vendors', { params: { limit: 100 } })).data;
    return ((Array.isArray(result) ? result : result?.data ?? []) as VendorChoice[]).filter((vendor) => vendor.isActive !== false);
  },
};

// ---------------------------------------------------------------- approve a vendor, or change the terms

const ApprovalForm: React.FC<{ approval: Approval | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ approval, isPending, onCancel, onSubmit }) => {
  const { data: vendors = [] } = useQuery({ queryKey: ['approvedVendors', 'vendors'], queryFn: avlApi.vendors, enabled: !approval });
  const [product, setProduct] = useState<ProductChoice | null>(null);
  const [form, setForm] = useState({
    vendorId: approval?.vendorId ?? '',
    vendorPartNumber: approval?.vendorPartNumber ?? '',
    leadTimeDays: approval?.leadTimeDays === null || approval?.leadTimeDays === undefined ? '' : String(approval.leadTimeDays),
    minimumOrderQuantity: approval?.minimumOrderQuantity === null || approval?.minimumOrderQuantity === undefined ? '' : String(approval.minimumOrderQuantity),
    validUntil: approval?.validUntil ?? '',
    isPreferred: approval?.isPreferred ?? false,
    isActive: approval?.isActive ?? true,
    notes: approval?.notes ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: choices = [], isFetching } = useQuery({ queryKey: ['approvedVendors', 'products', search], queryFn: () => avlApi.products(search), enabled: !approval && !product && search.length >= 2 });

  const set = (field: 'vendorId' | 'vendorPartNumber' | 'leadTimeDays' | 'minimumOrderQuantity' | 'validUntil' | 'notes') => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!approval && !product) next.product = 'Choose the item';
    if (!approval && !form.vendorId) next.vendorId = 'Choose the vendor';
    if (form.leadTimeDays.trim() !== '' && (!Number.isInteger(Number(form.leadTimeDays)) || Number(form.leadTimeDays) < 0)) next.leadTimeDays = 'Whole days';
    if (form.minimumOrderQuantity.trim() !== '' && (!Number.isFinite(Number(form.minimumOrderQuantity)) || Number(form.minimumOrderQuantity) < 0)) next.minimumOrderQuantity = 'Enter a quantity';
    if (!approval && form.validUntil && form.validUntil < today()) next.validUntil = 'Not in the past';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      ...(approval ? { isActive: form.isActive } : { productId: product!.id, vendorId: form.vendorId }),
      vendorPartNumber: form.vendorPartNumber.trim() || null,
      leadTimeDays: form.leadTimeDays.trim() === '' ? null : Number(form.leadTimeDays),
      minimumOrderQuantity: form.minimumOrderQuantity.trim() === '' ? null : Number(form.minimumOrderQuantity),
      validUntil: form.validUntil || null,
      isPreferred: form.isPreferred,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {approval ? (
        <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 ring-1 ring-gray-200">
          <span className="font-semibold text-gray-900">{approval.vendorName}</span> for <span className="font-semibold text-gray-900">{approval.productName}</span> ({approval.sku}), approved {formatDate(approval.approvedOn)}
        </p>
      ) : (
        <>
          <Field label="Item" htmlFor="av-product" required error={errors.product}>
            {product ? (
              <div className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-200">
                <p className="truncate text-sm font-medium text-gray-900">
                  {product.name} <span className="font-normal text-gray-500">{product.sku}</span>
                </p>
                <button type="button" onClick={() => setProduct(null)} className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-500">
                  Change
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input id="av-product" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU" className={cn(inputClass, 'pl-9', errors.product && inputErrorClass)} />
                </div>
                {search.length >= 2 && (
                  <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                    {choices.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
                    ) : (
                      choices.map((row) => (
                        <li key={row.id}>
                          <button type="button" onClick={() => { setProduct(row); setErrors((prev) => { const { product: _cleared, ...rest } = prev; return rest; }); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                            <span>
                              <span className="font-medium text-gray-900">{row.name}</span> <span className="text-gray-500">{row.sku}</span>
                            </span>
                            <span className="text-xs text-gray-500">{row.unit}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </Field>
          <Field label="Vendor" htmlFor="av-vendor" required error={errors.vendorId}>
            <select id="av-vendor" value={form.vendorId} onChange={set('vendorId')} className={cn(inputClass, errors.vendorId && inputErrorClass)}>
              <option value="">Choose…</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name} · {vendor.code}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Their part number" htmlFor="av-part">
          <input id="av-part" type="text" maxLength={80} value={form.vendorPartNumber} onChange={set('vendorPartNumber')} className={inputClass} />
        </Field>
        <Field label="Lead time (days)" htmlFor="av-lead" error={errors.leadTimeDays}>
          <input id="av-lead" type="number" min="0" step="1" value={form.leadTimeDays} onChange={set('leadTimeDays')} className={cn(inputClass, 'text-right tabular-nums', errors.leadTimeDays && inputErrorClass)} />
        </Field>
        <Field label="Minimum order quantity" htmlFor="av-moq" error={errors.minimumOrderQuantity}>
          <input id="av-moq" type="number" min="0" step={QUANTITY_STEP} value={form.minimumOrderQuantity} onChange={set('minimumOrderQuantity')} className={cn(inputClass, 'text-right tabular-nums', errors.minimumOrderQuantity && inputErrorClass)} />
        </Field>
        <Field label="Approved until" htmlFor="av-valid" error={errors.validUntil} hint="Optional. After this day the approval no longer counts.">
          <input id="av-valid" type="date" value={form.validUntil} onChange={set('validUntil')} className={cn(inputClass, errors.validUntil && inputErrorClass)} />
        </Field>
        <Field label="Notes" htmlFor="av-notes" className="sm:col-span-2">
          <input id="av-notes" type="text" maxLength={300} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input id="av-preferred" type="checkbox" checked={form.isPreferred} onChange={(event) => setForm((prev) => ({ ...prev, isPreferred: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        First choice for this item
      </label>
      {approval && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input id="av-active" type="checkbox" checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          The approval is in use
        </label>
      )}
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : approval ? 'Save Approval' : 'Approve Vendor'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- the page

const ApprovedVendors: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('vendors.create');
  const canEdit = can('vendors.update');
  const canDelete = can('vendors.delete');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Approval | null>(null);
  const [removing, setRemoving] = useState<Approval | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['approvedVendors', 'page', { page, limit, search, status: statusFilter }],
    queryFn: () => avlApi.page({ page, limit, search, status: statusFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['approvedVendors', 'summary'], queryFn: avlApi.summary });

  const refresh = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['approvedVendors', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => (editing ? avlApi.update(editing.id, input) : avlApi.create(input)),
    onSuccess: (approval) => {
      toast.success(editing ? 'Approval saved' : `${approval.vendorName} approved for ${approval.productName}`);
      refresh();
      closeForm();
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The approval could not be saved')),
  });
  const remove = useMutation({
    mutationFn: (approval: Approval) => avlApi.remove(approval.id),
    onSuccess: () => {
      toast.success('Vendor taken off the list');
      refresh();
      setRemoving(null);
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The vendor could not be removed')),
  });
  const restrict = useMutation({
    mutationFn: (approval: Approval) => avlApi.restrict(approval.productId, !approval.approvedVendorsOnly),
    onSuccess: (result: { productName: string; approvedVendorsOnly: boolean }) => {
      toast.success(result.approvedVendorsOnly ? `${result.productName} can now only be ordered from approved vendors` : `${result.productName} can be ordered from any vendor again`);
      refresh();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be changed')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Items with a list', value: formatNumber(summary?.itemsWithList ?? 0), hint: `${formatNumber(summary?.approvals ?? 0)} approvals in force` },
    { label: 'Restricted items', value: formatNumber(summary?.restrictedItems ?? 0), hint: 'Can only be ordered from approved vendors' },
    { label: 'Lapsed', value: formatNumber(summary?.lapsed ?? 0), hint: `${formatNumber(summary?.lapsingSoon ?? 0)} more lapse within 30 days` },
    { label: 'Cannot be ordered', value: formatNumber(summary?.restrictedWithoutVendor ?? 0), hint: 'Restricted items with no approval in force' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Approved Vendors</h1>
          <p className="mt-1 text-sm text-gray-500">Which vendors are approved to supply which item, on what terms. An item can be restricted, so that it is only ordered from its approved vendors.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Approve Vendor
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by item, vendor or vendor part number" aria-label="Search approved vendors" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Item</th>
                <th scope="col" className={thClass}>Vendor</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Lead time</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Min. order</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Last price</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The list could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'Nothing matches' : 'No vendor is approved for any item yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Approve a vendor for an item to start its list.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="max-w-xs px-4 py-3">
                      <p className="truncate text-sm font-medium text-gray-900">{row.productName}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {row.sku}
                        {row.approvedVendorsOnly && <span className={cn(badgeClass, 'ml-2 bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>Approved vendors only</span>}
                      </p>
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="flex items-center gap-1 truncate text-sm text-gray-900">
                        {row.isPreferred && <StarIcon className="h-4 w-4 shrink-0 fill-amber-400 text-amber-500" aria-label="First choice" />}
                        {row.vendorName}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{[row.vendorCode, row.vendorPartNumber ? `their part ${row.vendorPartNumber}` : null].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{row.leadTimeDays === null ? '—' : `${row.leadTimeDays} days`}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{row.minimumOrderQuantity === null ? '—' : `${formatNumber(row.minimumOrderQuantity)} ${row.unit}`}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {row.lastPrice === null ? '—' : formatMoney(row.lastPrice)}
                      {row.lastOrderedOn && <span className="block text-xs text-gray-500">{formatDate(row.lastOrderedOn)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span>
                      {row.validUntil && <span className="block text-xs text-gray-500">until {formatDate(row.validUntil)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {canEdit && (
                          <>
                            <button type="button" onClick={() => restrict.mutate(row)} disabled={restrict.isPending} className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50" aria-label={`${row.approvedVendorsOnly ? 'Allow any vendor for' : 'Allow only approved vendors for'} ${row.productName}`}>
                              {row.approvedVendorsOnly ? 'Allow Any Vendor' : 'Restrict Item'}
                            </button>
                            <button type="button" onClick={() => { setEditing(row); setFormOpen(true); }} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Edit ${row.vendorName} for ${row.productName}`}>
                              Edit
                            </button>
                          </>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => setRemoving(row)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50" aria-label={`Remove ${row.vendorName} from ${row.productName}`}>
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title={editing ? 'Edit Approval' : 'Approve a Vendor for an Item'} size="lg">
        {formOpen && <ApprovalForm key={editing?.id ?? 'new'} approval={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />}
      </Modal>

      <ConfirmDialog
        isOpen={!!removing}
        title="Take the vendor off the list?"
        message={removing ? `${removing.vendorName} will no longer be an approved vendor for ${removing.productName}.` : ''}
        confirmLabel="Remove"
        onConfirm={() => removing && remove.mutate(removing)}
        onClose={() => setRemoving(null)}
        isPending={remove.isPending}
      />
    </div>
  );
};

export default ApprovedVendors;
